import { DetailPageLayout, createDefaultDetailPageTitleFn } from '@app/DetailPageLayout';
import { getAccessMode } from '@common/detailPage.hooks';
import { useAppConfigs, useProjectId, useProjectIsArchived } from '@config/config.hooks';
import {
  CircularProgressBar,
  FileUploader,
  IBreadcrumbItem,
  IFileUploaderValue,
  IconName,
  Scrollbar,
  ToastMessage,
  isNullOrWhiteSpace,
  toastMessage,
  useMsal,
} from '@gcl/react-core';
import { useNavigate } from '@routes/routes.hooks';
import { routes } from '@routes/routes.models';
import { getTransmittalItemRoute } from '@transmittal/detailPage/transmittalDetailPage.hooks';
import { ETransmittalType } from '@transmittal/transmittal.constants';
import { useFetchTransmittalById } from '@transmittal/transmittal.queries';
import React, { useMemo, useState } from 'react';
import './TransmittalBulkUploadPage.scss';
import {
  downloadDrawingBulkUploadTemplateAsync,
  downloadFile,
  uploadProjectFileAsync,
} from '@file/projectFile/projectFile.services';
import { envConfigs } from '@config/envConfigs';
import { v4 as uuidv4 } from 'uuid';
import jsZip from 'jszip';
import * as XLSX from 'xlsx';
import { IDownloadDrawingBulkUploadTemplate, IFileResponse } from '@file/projectFile/projectFile.models';
import { useMutation } from '@tanstack/react-query';
import { AxiosErrorResponse } from '@core/error.models';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { EProjectFileType } from '@file/projectFile/projectFile.constants';
import { getMasterScheduleDetail } from '@masterSchedule/masterSchedule.hooks';
import { MACOSX_PREFIX } from '@common/constants';
import {
  BULK_UPLOAD_XLSX_FILENAME,
  BULK_UPLOAD_XLSX_FILETYPE,
  ETransmittalBulkUploadJsonKey,
} from './transmittalBulkUploadPage.constants';

export const TransmittalBulkUploadPage = () => {
  const appConfigs = useAppConfigs();
  const { id, mode } = getAccessMode();
  const projectId = useProjectId();
  const isProjectArchived = useProjectIsArchived();

  const { instance } = useMsal();

  const { navigate, projectNo } = useNavigate();

  const [fileUploaderValue, setFileUploaderValue] = React.useState<number | undefined>(undefined);
  const [templateDownloadValue, setTemplateDownloadValue] = useState<number | undefined>(undefined);

  const {
    data: transmittalDetails,
    isLoading: isLoadingTransmittalDetails,
    isRefetching: isRefetchingTransmittalDetails,
    error: errorGetTransmittalDetails,
  } = useFetchTransmittalById({ projectId }, id);

  const { masterSchedule } = getMasterScheduleDetail(projectId, transmittalDetails?.masterScheduleId);

  const {
    mutate: mutateDownloadTemplate,
    error: errorDownloadTemplate,
    isError: isErrorDownloadTemplate,
    reset: resetDownloadTemplate,
  } = useMutation<IFileResponse, AxiosErrorResponse, IDownloadDrawingBulkUploadTemplate>(
    downloadDrawingBulkUploadTemplateAsync,
    {
      onSuccess: (data, variables, context) => {
        setTemplateDownloadValue(0);
        downloadFile(data.file, data.filename);
      },
    },
  );

  const transmittalName = React.useMemo(() => {
    switch (transmittalDetails?.type) {
      case ETransmittalType.DESIGNER:
        return 'Designer';
      case ETransmittalType.SITE:
        return 'Site';
      case ETransmittalType.ICE:
        return 'Checker';
      case ETransmittalType.REVIEWER:
        return 'Reviewer';

      default:
        return undefined;
    }
  }, [transmittalDetails?.type]);

  const breadcrumbItems: IBreadcrumbItem[] = useMemo(() => {
    return [
      {
        text: `${projectNo} - Home`,
        prefixIconName: 'House',
        onClick: () => {
          navigate(routes.t1.path);
        },
      },
      {
        text: `Search ${transmittalName}`,
        prefixIconName: 'Search' as IconName,
        onClick: () => {
          if (transmittalDetails?.type === ETransmittalType.DESIGNER) {
            navigate(routes.transmittals.designerTransmittal.path);
          } else if (transmittalDetails?.type === ETransmittalType.SITE) {
            navigate(routes.transmittals.siteTransmittal.path);
          } else if (transmittalDetails?.type === ETransmittalType.ICE) {
            navigate(routes.transmittals.checkerTransmittal.path);
          } else if (transmittalDetails?.type === ETransmittalType.REVIEWER) {
            navigate(routes.transmittals.lirTransmittal.path);
          }
        },
      },
      {
        text: `View ${transmittalName ?? 'Transmittal'}`,
        prefixIconName: 'FileText' as IconName,
        onClick: () => {
          navigate(getTransmittalItemRoute(transmittalDetails?.type), { id: (id || 0).toString() });
        },
      },
      {
        text: 'Bulk Upload',
        active: true,
      },
    ];
  }, [transmittalDetails?.type]);

  const fileValidation = async (jsonData: { [key: string]: any }[], zipFiles: [string, jsZip.JSZipObject][]) => {
    const filenamesInZip = zipFiles
      .map(([filename, value]) => filename)
      .filter(
        (filename) =>
          !filename.startsWith(MACOSX_PREFIX) &&
          !(filename.startsWith(BULK_UPLOAD_XLSX_FILENAME) && filename.endsWith(BULK_UPLOAD_XLSX_FILETYPE)),
      );

    const filenamesInJson = jsonData.map((json) => {
      let filename = String(json[ETransmittalBulkUploadJsonKey.FILE_NAME] ?? '');
      if (!filename.endsWith('.pdf')) filename = `${filename}.pdf`;
      return filename;
    });
    const errorFromJsonData = jsonData.map((json, index) => {
      const desc = String(json[ETransmittalBulkUploadJsonKey.DESCRIPTION] ?? '');
      const docNo = String(json[ETransmittalBulkUploadJsonKey.DOC_NO] ?? '');
      const revNo = String(json[ETransmittalBulkUploadJsonKey.REV_NO] ?? '');
      let filename = String(json[ETransmittalBulkUploadJsonKey.FILE_NAME] ?? '');
      if (!isNullOrWhiteSpace(filename) && !filename.endsWith('.pdf')) filename = `${filename}.pdf`;

      if (isNullOrWhiteSpace(desc)) return `Row ${index + 1}: Description cannot be empty.`;
      if (isNullOrWhiteSpace(docNo)) return `Row ${index + 1}: Document Number cannot be empty.`;

      if (!isNullOrWhiteSpace(filename) && isNullOrWhiteSpace(revNo))
        return `Row ${index + 1}: Revision Number cannot be empty.`;
      if (!isNullOrWhiteSpace(revNo) && isNullOrWhiteSpace(filename))
        return `Row ${index + 1}: Upload File Name cannot be empty.`;
      if (
        !isNullOrWhiteSpace(filename) &&
        !filenamesInZip.find((filenameInZip) => {
          return filenameInZip === filename;
        })
      )
        return `Row ${index + 1}: Cannot find ${filename} in zip.`;

      return undefined;
    });

    const errorFilesInZipNotFoundInJson = filenamesInZip
      .filter((filenameInZip) => {
        return !filenamesInJson.find((filenameInJson) => filenameInZip === filenameInJson);
      })
      .map((filename) => `Cannot find data of ${filename} in .xlsx.`);

    const errorFilesInZipInvalidType = filenamesInZip.find((filenameInZip) => !filenameInZip.endsWith('.pdf'))
      ? 'Only .pdf is allowed to upload.'
      : '';

    const errorMsg = errorFromJsonData
      .concat(errorFilesInZipNotFoundInJson)
      .join('\n')
      .concat(errorFilesInZipInvalidType);

    if (!isNullOrWhiteSpace(errorMsg)) throw new Error(errorMsg);

    return true;
  };

  const onChangeFile = async (newValue: IFileUploaderValue | null) => {
    try {
      if (!newValue) throw new Error('No file uploaded');
      const file = newValue?.files[0];

      let zip: jsZip;
      try {
        zip = await jsZip.loadAsync(file);
      } catch (error) {
        throw new Error('Cannot load zip file');
      }
      Object.entries(zip.files)
        .filter(([filename, value]) => filename.startsWith(MACOSX_PREFIX))
        .forEach(([filename, value]) => (zip as jsZip).remove(filename));
      const filteredZipBlob = await zip.generateAsync({ type: 'blob' });
      const clonedFile = new File([filteredZipBlob], file.name, { type: 'application/zip' });

      const filtered = Object.entries(zip.files)
        .filter(
          ([filename, value]) =>
            filename.startsWith(BULK_UPLOAD_XLSX_FILENAME) && filename.endsWith(BULK_UPLOAD_XLSX_FILETYPE),
        )
        .map(([filename, value]) => value);

      if (filtered.length === 0)
        throw new Error(`Cannot find ${BULK_UPLOAD_XLSX_FILENAME}.${BULK_UPLOAD_XLSX_FILETYPE}`);
      const xlsx = filtered[0];
      const data = await xlsx.async('arraybuffer');
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        defval: null,
      });

      const isValid = await fileValidation(jsonData as { [key: string]: any }[], Object.entries(zip.files));
      if (!isValid) throw new Error('Invalid data');

      const onUploadProgress = (progress: number) => {
        setFileUploaderValue(progress);
      };
      await uploadProjectFileAsync(
        {
          request: {
            file: clonedFile,
            projectId,
            uuid: uuidv4(),
            fileName: clonedFile.name,
            isTemp: false,
            fileType: EProjectFileType.DESIGNER_TRANSMITTAL_DRAWING,
            transmittalId: id,
          },
          onUploadProgress,
        },
        {
          apiTokenProps: {
            scopes: [envConfigs.apiScope as string],
            instance,
          },
          traceId: appConfigs.configs.traceId,
        },
        '/ProjectFiles/Drawings/BulkUpload/AddChunks',
        '/ProjectFiles/Drawings/BulkUpload/AddChunks/Complete',
      );
      toastMessage(
        <ToastMessage
          message="Upload success"
          toastOptions={{ type: 'success', closeOnClick: true, autoClose: 3000 }}
        />,
      );
      navigate(getTransmittalItemRoute(transmittalDetails?.type), { id });
    } catch (e: any) {
      const error = e as AxiosErrorResponse;
      const rowErrors = Object.keys(error.response?.data.errors ?? {})
        .map((row) => {
          return `Row ${row}: ${error.response?.data.errors?.[row]}`;
        })
        .join('\n');

      let errorMsg = 'Upload fail';
      if (rowErrors.length > 0) errorMsg = rowErrors;
      else if (error.message && typeof error.message === 'string') errorMsg = error.message;

      toastMessage(
        <ToastMessage message={errorMsg} toastOptions={{ type: 'error', closeOnClick: true, autoClose: false }} />,
      );
      setFileUploaderValue(undefined);
    }
  };

  return (
    <Scrollbar>
      <DetailPageLayout
        errorGetDetail={errorGetTransmittalDetails}
        isDrawer={false}
        data={mode === 'Create' ? {} : transmittalDetails}
        isRefetching={isRefetchingTransmittalDetails}
        isLoading={isLoadingTransmittalDetails}
        title={createDefaultDetailPageTitleFn(
          <>
            <span className="gcl-my-sm">{`Bulk Upload to ${transmittalName}${
              mode !== 'Create' && transmittalDetails?.transmittalNo ? ` - ${transmittalDetails?.transmittalNo}` : ''
            }`}</span>
          </>,
        )}
        breadcrumb={breadcrumbItems}
        render={() => {
          return (
            <>
              <ApiErrorDisplay
                errors={[{ error: errorDownloadTemplate, genericError: 'Download fail', reset: resetDownloadTemplate }]}
                callbackAfterToast={() => {
                  if (isErrorDownloadTemplate) {
                    setTemplateDownloadValue(undefined);
                  }
                }}
              />
              <ul>**For update exisiting documents only**</ul>
              <ol>
                <li>
                  <span
                    className="gcl-gems-transmittal-bulk-upload-download-template"
                    onClick={() => {
                      mutateDownloadTemplate({
                        projectId,
                        masterScheduleId: transmittalDetails?.masterScheduleId ?? -1,
                        transmittalId: id ?? -1,
                        onDownloadProgressUpdate: (percent) => {
                          setTemplateDownloadValue(percent);
                        },
                      });
                    }}
                  >
                    Download Excel Template
                    {!!templateDownloadValue && (
                      <CircularProgressBar
                        value={templateDownloadValue}
                        className="gcl-gems-transmittal-bulk-upload-progress-bar"
                      />
                    )}
                  </span>{' '}
                  for this transmittal package.
                </li>
                <li>
                  Fill in Upload File Name and revision number for each item to be updated. Leave blank or delete for
                  other items.
                </li>
                <li>
                  Create a ZIP file containing the Excel file with filename BulkUploadList.xlsx and the files to be
                  uploaded. Filenames must match the details entered in the excel.
                </li>
                <li>Upload the ZIP file.</li>
                <li>Press &apos;Finished&apos; button to review the updated transmittal.</li>
              </ol>
              <div className="gcl-gems-transmittal-bulk-upload-file-upload-container">
                <FileUploader
                  onChange={onChangeFile}
                  acceptFileTypes={['.zip']}
                  disabled={isProjectArchived || masterSchedule?.isLocked || masterSchedule?.isCancelled}
                />
                {fileUploaderValue && (
                  <CircularProgressBar
                    value={fileUploaderValue}
                    className="gcl-gems-transmittal-bulk-upload-progress-bar"
                  />
                )}
              </div>
            </>
          );
        }}
      />
    </Scrollbar>
  );
};
